import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CompanyService from "../../../Services/Company.service";
import {
  IOnboardingServices,
  IOnboardingWizardState,
} from "../../../Interfaces/IOnboardingWizardState";
import { useWriteThrough, WriteThroughResult } from "../useWriteThrough";
import HealthTickCard from "./HealthTickCard";
import HealthTickReviewCard from "../HealthTickReviewCard";
import DrugTestServicesTable from "./DrugTestServicesTable";
import SpecialistOccasionCards from "./SpecialistOccasionCards";
import {
  emptyServices,
  hairReasonCodesFrom,
  includesHealthTick,
  includesTdda,
  isAlcoholInPlay,
  isLabChosenByClient,
  METHOD_ORAL_FLUID,
  METHOD_URINE,
  optionLabel,
  OTHER_SERVICE_POLICY_DEVELOPMENT,
  OTHER_SERVICE_TRAINING,
  specialistCardsNeedTestReason,
  startDateServices,
  withAllOccasions,
  withPrefilledTestReasons,
} from "./Step2.rules";

type Props = {
  state: IOnboardingWizardState;
  companyRowID: string;
  frozen: boolean;
  registerFlush: (flush: () => Promise<WriteThroughResult>) => void;
  /** Reloads the wizard shell's own state — passed through to the HealthTick review card, whose
   * save needs the shell's copy of `healthTickReview` to move on (bug 5833). */
  onChanged: () => void;
};

/**
 * Wizard step 2 — Services and Testing (Feature #5614, remodelled by Epic 6303 Feature CRM2).
 *
 * The service line decides which halves of the step are shown; a hidden half is neither required nor
 * validated and keeps everything it was given (S2.1). HealthTick is offered only to New Zealand
 * clients (decision 2). Everything writes through as it is captured.
 *
 * The testing matrix is a list of named services (bug 6364) rendered by DrugTestServicesTable; the
 * client-level panel is gone because Imperans holds the device per service (bug 6368), and standalone
 * alcohol is an alcohol-only service in the same list rather than a separate tick (S2.6).
 */
const Step2Services: React.FC<Props> = ({
  state,
  companyRowID,
  frozen,
  registerFlush,
  onChanged,
}) => {
  const references = state.references;
  const context = references.step2Context;
  const hairReasonCodes = hairReasonCodesFrom(state);

  const [services, setServices] = useState<IOnboardingServices>(
    state.services
      ? withAllOccasions(state.services, hairReasonCodes)
      : emptyServices(hairReasonCodes, context),
  );

  const writer = useWriteThrough<{ services: IOnboardingServices }>((patch) =>
    CompanyService.saveOnboardingDraft({ companyRowID, services: patch.services }),
  );

  useEffect(() => {
    registerFlush(() => writer.flush());
  }, [registerFlush, writer]);

  /**
   * A brand-new client's service line defaults to TDDA, and the radio renders it correctly — but
   * that default only ever lived in this component's own local state until something wrote through
   * it (bug 5834). Committing the rendered default the moment it is known to be unsaved fixes it.
   */
  useEffect(() => {
    if (frozen) return;
    if (!state.services) writer.schedule({ services });
    // Mount only: this is a one-time commit of the initial default, not a sync with `services`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (changes: Partial<IOnboardingServices>) => {
    const next = { ...services, ...changes };
    setServices(next);
    if (!frozen) writer.schedule({ services: next });
  };

  // NZ Oral fluid confirmation lab is locked to CHL. Seed it if it is empty (bug 5678).
  useEffect(() => {
    if (frozen) return;
    if (context.oralFluidLabLocked && !services.oralFluidLab && context.defaultOralFluidLab) {
      update({ oralFluidLab: context.defaultOralFluidLab });
    }
  }, [frozen, context, services.oralFluidLab]);

  /**
   * A country change to Australia is gated on step 2 the moment it takes effect (decision 2); an
   * already-HealthTick-inclusive client is corrected automatically rather than left on a
   * disabled-but-checked radio (bug 5824).
   */
  useEffect(() => {
    if (frozen) return;
    if (!state.isHealthTickEligible && includesHealthTick(services.serviceLine)) {
      update({ serviceLine: "TDDA" });
    }
  }, [frozen, state.isHealthTickEligible, services.serviceLine]);

  /**
   * A hair/nail Pre Defined card's Test Reason prefills with the reason name while empty (S2.7,
   * bug 5857) — a real stored value, not the placeholder alone.
   */
  useEffect(() => {
    if (frozen) return;
    const hairNeeds = specialistCardsNeedTestReason(services.hairOccasions);
    const nailNeeds = specialistCardsNeedTestReason(services.nailOccasions);
    if (!hairNeeds && !nailNeeds) return;
    update({
      hairOccasions: hairNeeds
        ? withPrefilledTestReasons(services.hairOccasions, references.step2HairTestReasons)
        : services.hairOccasions,
      nailOccasions: nailNeeds
        ? withPrefilledTestReasons(services.nailOccasions, references.step2HairTestReasons)
        : services.nailOccasions,
    });
  }, [frozen, services.hairOccasions, services.nailOccasions, references.step2HairTestReasons]);

  /**
   * The cut-off bands prefill from the client's country, but only while they are empty — a figure
   * someone has typed is never overwritten (S2.10).
   */
  useEffect(() => {
    if (frozen || !isAlcoholInPlay(services)) return;
    if (services.alcoholBandUnder20 || services.alcoholBand20Plus) return;
    update({
      alcoholBandUnder20: context.alcoholDrivingLimitUnder20,
      alcoholBand20Plus: context.alcoholDrivingLimit20Plus,
    });
  }, [services.drugTestServices, services.urineEnabled, services.oralFluidEnabled]);

  const showTdda = includesTdda(services.serviceLine);
  const showHealthTick =
    includesHealthTick(services.serviceLine) && state.isHealthTickEligible;

  const methodLabel = (key: string) => optionLabel(references.step2Methods, key);

  /**
   * Ticking a screened method also seeds its confirmation lab from the default the server resolved
   * for this country (bugs 5678, 5679). Only an empty lab is seeded; Australia seeds nothing because
   * the laboratory follows each office (task 6008).
   */
  const toggleMethod = (method: string, field: string, checked: boolean) => {
    const patch: any = { [field]: checked };

    if (checked && isLabChosenByClient(context)) {
      if (method === METHOD_URINE && !services.urineLab && context.defaultUrineLab)
        patch.urineLab = context.defaultUrineLab;
      if (method === METHOD_ORAL_FLUID && !services.oralFluidLab && context.defaultOralFluidLab)
        patch.oralFluidLab = context.defaultOralFluidLab;
    }

    update(patch);
  };

  const anythingSelected =
    services.urineEnabled ||
    services.oralFluidEnabled ||
    services.hairEnabled ||
    services.nailEnabled ||
    (services.drugTestServices || []).some((x) => x.method === "Alcohol") ||
    services.otherServices?.policyDevelopment ||
    services.otherServices?.training ||
    services.otherServices?.other;

  return (
    <>
      {/* Service line (S2.1) */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
        <FormLabel sx={{ fontSize: 13 }}>Service line</FormLabel>
        <RadioGroup
          row
          value={services.serviceLine || "TDDA"}
          onChange={(e) => update({ serviceLine: e.target.value })}
        >
          <FormControlLabel value="TDDA" control={<Radio size="small" disabled={frozen} />} label="TDDA" />
          <Tooltip
            title={
              state.isHealthTickEligible
                ? ""
                : "HealthTick is available to New Zealand clients only."
            }
          >
            <span>
              <FormControlLabel
                value="HealthTick"
                control={<Radio size="small" disabled={frozen || !state.isHealthTickEligible} />}
                label="HealthTick"
              />
            </span>
          </Tooltip>
          <Tooltip
            title={
              state.isHealthTickEligible
                ? ""
                : "HealthTick is available to New Zealand clients only."
            }
          >
            <span>
              <FormControlLabel
                value="Both"
                control={<Radio size="small" disabled={frozen || !state.isHealthTickEligible} />}
                label="Both"
              />
            </span>
          </Tooltip>
        </RadioGroup>
        <Typography variant="caption" color="text.secondary">
          A hidden half keeps whatever was captured in it — switching back shows it unchanged.
        </Typography>
      </Paper>

      {showTdda && (
        <>
          {/* Collection methods (S2.2) */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
            <FormLabel sx={{ fontSize: 13 }}>Collection methods</FormLabel>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 0.5 }}>
              {[
                [METHOD_URINE, services.urineEnabled, "urineEnabled"],
                [METHOD_ORAL_FLUID, services.oralFluidEnabled, "oralFluidEnabled"],
                ["Hair", services.hairEnabled, "hairEnabled"],
                ["Nail", services.nailEnabled, "nailEnabled"],
              ].map(([key, checked, field]) => (
                <Box key={key as string} sx={{ maxWidth: 260 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        disabled={frozen}
                        checked={Boolean(checked)}
                        onChange={(e) =>
                          toggleMethod(key as string, field as string, e.target.checked)
                        }
                      />
                    }
                    label={methodLabel(key as string)}
                  />
                  {/* Inline, never hover-only (S2.13). */}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {references.step2Methods.find((x) => x.key === key)?.description}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* The blocking message shows here as well as against the step navigation (S2.2). */}
            {!anythingSelected && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Select at least one collection method. An alcohol-only testing service or another
                service satisfies this step too.
              </Alert>
            )}
          </Paper>

          {/* The testing services: one card per named service (bug 6364). Shown as soon as TDDA is in
              the line, because an alcohol-only service needs no collection method ticked (S2.6). */}
          <DrugTestServicesTable
            services={services}
            onChange={update}
            references={references}
            context={context}
            frozen={frozen}
          />

          {services.hairEnabled && (
            <SpecialistOccasionCards
              method="Hair"
              cards={services.hairOccasions}
              onChange={(cards) => update({ hairOccasions: cards })}
              occasions={references.step2HairTestReasons}
              testTypes={references.step2SpecialistTestTypes}
              panels={references.step2HairPanels}
              periods={references.step2SpecialistPeriods}
              fixedLab={context.hairLab}
              frozen={frozen}
            />
          )}

          {services.nailEnabled && (
            <SpecialistOccasionCards
              method="Nail"
              cards={services.nailOccasions}
              onChange={(cards) => update({ nailOccasions: cards })}
              occasions={references.step2HairTestReasons}
              testTypes={references.step2SpecialistTestTypes}
              panels={references.step2NailPanels}
              periods={references.step2SpecialistPeriods}
              fixedLab={context.nailLab}
              frozen={frozen}
            />
          )}

          {/* Alcohol cut-off bands, prefilled from the country only while empty (S2.10). The unit is
              the one the request converts from, so it is always shown (bug 6369). */}
          {isAlcoholInPlay(services) && (
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Alcohol cut-offs
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
                {[
                  ["Under 20 years", services.alcoholBandUnder20, "alcoholBandUnder20"],
                  ["20 years and over", services.alcoholBand20Plus, "alcoholBand20Plus"],
                ].map(([label, value, field]) => (
                  <Autocomplete
                    key={field as string}
                    freeSolo
                    disabled={frozen}
                    options={context.alcoholSuggestions}
                    inputValue={(value as string) || ""}
                    onInputChange={(_, next, reason) => {
                      if (reason === "reset") return;
                      update({ [field as string]: next } as any);
                    }}
                    onChange={(_, selected) =>
                      update({ [field as string]: (selected as string) || "" } as any)
                    }
                    sx={{ minWidth: 240 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        required
                        label={`${label} (${context.alcoholUnit})`}
                      />
                    )}
                  />
                ))}
                <Button
                  size="small"
                  disabled={frozen}
                  onClick={() =>
                    update({
                      alcoholBandUnder20: context.alcoholDrivingLimitUnder20,
                      alcoholBand20Plus: context.alcoholDrivingLimit20Plus,
                    })
                  }
                >
                  Reset to driving limits
                </Button>
              </Box>
            </Paper>
          )}

          {/* Other services (S2.18) — selecting one satisfies step 2 with no method ticked. */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Other services
            </Typography>
            {references.step2OtherServices.map((option) => {
              const current = services.otherServices || {
                policyDevelopment: false,
                training: false,
                other: false,
                otherDescription: null,
              };
              const field =
                option.key === OTHER_SERVICE_POLICY_DEVELOPMENT
                  ? "policyDevelopment"
                  : option.key === OTHER_SERVICE_TRAINING
                    ? "training"
                    : "other";
              return (
                <Box key={option.key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        disabled={frozen}
                        checked={Boolean((current as any)[field])}
                        onChange={(e) =>
                          update({
                            otherServices: { ...current, [field]: e.target.checked } as any,
                          })
                        }
                      />
                    }
                    label={option.label}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                    {option.description}
                  </Typography>
                </Box>
              );
            })}
            {services.otherServices?.other && (
              <TextField
                fullWidth
                size="small"
                label="Describe the other service"
                sx={{ mt: 1 }}
                disabled={frozen}
                value={services.otherServices.otherDescription ?? ""}
                onChange={(e) =>
                  update({
                    otherServices: {
                      ...services.otherServices,
                      otherDescription: e.target.value,
                    },
                  })
                }
              />
            )}
          </Paper>
        </>
      )}

      {showHealthTick && (
        <HealthTickCard
          state={state}
          services={services}
          healthTick={
            services.healthTick ?? {
              pemSelected: false,
              ahmSelected: false,
              fluSelected: false,
              dl9Selected: false,
              pemLevels: [],
              ahmLevels: [],
              pemCustomNotes: null,
              ahmCustomNotes: null,
              pemRoles: [],
              ahmRoles: [],
              htPreEmployment: null,
              otherHealthTickServices: null,
            }
          }
          onChange={(healthTick) => update({ healthTick })}
          context={context}
          frozen={frozen}
        />
      )}

      {/* The review belongs to the HealthTick half of the step (story 5590); gated on this
          component's own live service line so ticking Both mounts the card immediately (bug 5833). */}
      {state.isHealthTickEligible && (showHealthTick || Boolean(state.healthTickReview)) && (
        <HealthTickReviewCard
          state={state}
          companyRowID={companyRowID}
          onChanged={onChanged}
          frozen={frozen}
        />
      )}

      {/* Start dates and the urgency flag (S2.19). Neither ever blocks. */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0" }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Start dates and urgency
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              disabled={frozen}
              checked={services.isUrgentSetup}
              onChange={(e) => update({ isUrgentSetup: e.target.checked })}
            />
          }
          label="Urgent setup"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          Flagged to whoever picks the setup up, printed on the SOP, and sets the Imperans review
          ticket's priority.
        </Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
          {startDateServices(services).map((service) => (
            <TextField
              key={service.key}
              type="date"
              size="small"
              label={`${service.label} start date`}
              sx={{ minWidth: 220 }}
              InputLabelProps={{ shrink: true }}
              disabled={frozen}
              // A native date input only accepts yyyy-MM-dd; the stored value round-trips as a full
              // UTC instant, and only the calendar date is meaningful here.
              value={((services.serviceStartDates || {})[service.key] || "").slice(0, 10)}
              onChange={(e) =>
                update({
                  serviceStartDates: {
                    ...(services.serviceStartDates || {}),
                    [service.key]: e.target.value || null,
                  },
                })
              }
            />
          ))}
          {!startDateServices(services).length && (
            <Typography variant="caption" color="text.secondary">
              Select a service above to give it a start date.
            </Typography>
          )}
        </Box>
      </Paper>

      {frozen && (
        <Alert severity="info">
          This onboarding has been submitted, so step 2 is read-only.
        </Alert>
      )}
    </>
  );
};

export default Step2Services;
